import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Bug,
  Gauge,
  ShieldAlert,
  Link2,
  Network,
  Globe,
  FileText,
  X,
  Package,
  Server,
  Box,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   DUMMY DATA — remove when the backend is wired up.
   The focal CVE always lives in cols[0]. No other column contains
   a CVE node. Severity and CVSS score are attributes on the CVE
   itself, not separate nodes.
   ═══════════════════════════════════════════════════════════════ */
const DUMMY_DATA = [
  {
    title:
      "Threat actors are actively exploiting CVE-2025-53521 in F5 BIG-IP, allowing remote code execution and system compromise.",
    timestamp: 1775718548,
    report:
      "Hackers are actively exploiting a critical vulnerability (CVE-2025-53521) in F5 BIG-IP Access Policy Manager (APM).\n\nWith over 240,000 BIG-IP instances exposed online, the risk remains high.\n\nF5 advises defenders to conduct thorough forensic reviews.",
    cols: [
      [
        {
          node_id: 11,
          type: "CVE",
          name: "CVE-2025-53521",
          active: true,
          severity: "MEDIUM",
          cvss_score: 6.5,
        },
      ],
      [
        {
          node_id: 14,
          type: "REFERENCE",
          name: "bigip-admin.f5.com",
          active: true,
        },
        { node_id: 15, type: "REFERENCE", name: "dot.ca.gov", active: true },
      ],
      [{ node_id: 20, type: "ASN", name: "AS1348", active: true }],
      [{ node_id: 21, type: "IP", name: "dot.ca.gov", active: true }],
    ],
    links: [
      [
        { source: 11, target: 14, active: true, verb: "references" },
        { source: 11, target: 15, active: true, verb: "affects" },
      ],
      [
        { source: 14, target: 20, active: true, verb: "hosted on" },
        { source: 15, target: 20, active: true, verb: "hosted on" },
      ],
      [{ source: 20, target: 21, active: true, verb: "resolves to" }],
      [],
    ],
  },
  {
    title:
      "Critical authentication bypass CVE-2025-32975 actively exploited against Quest KACE Systems Management Appliance.",
    timestamp: 1774547708,
    report:
      "Arctic Wolf observed active exploitation of CVE-2025-32975, a critical authentication bypass vulnerability in Quest KACE Systems Management Appliance.\n\nThe attack chain included command execution, credential harvesting, lateral movement to backup infrastructure and domain controllers, and persistence mechanisms through new admin accounts.",
    cols: [
      [
        {
          node_id: 31,
          type: "CVE",
          name: "CVE-2025-32975",
          active: true,
          severity: "CRITICAL",
          cvss_score: 9.1,
        },
      ],
      [
        {
          node_id: 34,
          type: "REFERENCE",
          name: "Quest KACE SMA",
          active: true,
        },
        {
          node_id: 35,
          type: "REFERENCE",
          name: "arcticwolf.com",
          active: true,
        },
        {
          node_id: 36,
          type: "REFERENCE",
          name: "kace.quest.com",
          active: false,
        },
      ],
      [
        { node_id: 37, type: "ASN", name: "AS15169", active: true },
        { node_id: 38, type: "ASN", name: "AS8075", active: false },
      ],
      [
        { node_id: 39, type: "IP", name: "arcticwolf.com", active: true },
        { node_id: 40, type: "IP", name: "kace.quest.com", active: false },
      ],
    ],
    links: [
      [
        { source: 31, target: 34, active: true, verb: "affects" },
        { source: 31, target: 35, active: true, verb: "references" },
        { source: 31, target: 36, active: false, verb: "references" },
      ],
      [
        { source: 34, target: 37, active: false, verb: "hosted on" },
        { source: 35, target: 37, active: true, verb: "hosted on" },
        { source: 36, target: 38, active: false, verb: "hosted on" },
      ],
      [
        { source: 37, target: 39, active: true, verb: "resolves to" },
        { source: 38, target: 40, active: false, verb: "resolves to" },
      ],
      [],
    ],
  },
  {
    title:
      "CVE-UNKNOWN discovered in widely-used npm package axios, downloaded over 50 million times weekly.",
    timestamp: 1775715741,
    report:
      "A supply chain attack targeting the popular npm package axios was discovered.\n\nThese versions introduced a heavily obfuscated dropper that silently installed a Remote Access Trojan on developer machines.\n\nWith axios drawing over 50 million weekly downloads, even a brief exposure window posed significant risk.",
    cols: [
      [
        {
          node_id: 51,
          type: "CVE",
          name: "CVE-UNKNOWN",
          active: false,
          cvss_score: 8.8,
        },
      ],
      [
        { node_id: 53, type: "REFERENCE", name: "axios", active: true },
        { node_id: 54, type: "REFERENCE", name: "npmjs.com", active: true },
      ],
      [{ node_id: 55, type: "ASN", name: "AS16509", active: true }],
      [{ node_id: 56, type: "IP", name: "registry.npmjs.org", active: true }],
    ],
    links: [
      [
        { source: 51, target: 53, active: true, verb: "affects" },
        { source: 51, target: 54, active: true, verb: "references" },
      ],
      [
        { source: 53, target: 55, active: true, verb: "hosted on" },
        { source: 54, target: 55, active: true, verb: "hosted on" },
      ],
      [{ source: 55, target: 56, active: true, verb: "resolves to" }],
      [],
    ],
  },
  {
    title:
      "CVE-2026-21643 discovered in Fortinet FortiClient EMS, enabling SQL injection and privilege escalation.",
    timestamp: 1775718342,
    report:
      "Active exploitation of CVE-2026-21643 in Fortinet's FortiClient EMS has been observed.\n\nAdversaries leverage improper sanitization of HTTP requests to inject malicious SQL commands.\n\nShodan scans indicate approximately 1,000 instances are publicly exposed.",
    cols: [
      [
        {
          node_id: 71,
          type: "CVE",
          name: "CVE-2026-21643",
          active: true,
          severity: "HIGH",
          cvss_score: 8.1,
        },
      ],
      [
        {
          node_id: 74,
          type: "REFERENCE",
          name: "Fortinet FortiClient EMS",
          active: true,
        },
      ],
      [{ node_id: 75, type: "ASN", name: "AS32934", active: true }],
      [{ node_id: 76, type: "IP", name: "fortinet.com", active: true }],
    ],
    links: [
      [{ source: 71, target: 74, active: true, verb: "affects" }],
      [{ source: 74, target: 75, active: true, verb: "hosted on" }],
      [{ source: 75, target: 76, active: true, verb: "resolves to" }],
      [],
    ],
  },
  {
    title:
      "CVE-2025-44190 zero-day exploited in Apache Tomcat deployments, enabling remote code execution.",
    timestamp: 1775620000,
    report:
      "A zero-day vulnerability in Apache Tomcat's JSP handler has been observed under active exploitation.\n\nAttackers can trigger deserialization of attacker-controlled payloads to achieve RCE.\n\nPatches are available from the Apache Software Foundation.",
    cols: [
      [
        {
          node_id: 81,
          type: "CVE",
          name: "CVE-2025-44190",
          active: true,
          severity: "CRITICAL",
          cvss_score: 9.8,
        },
      ],
      [
        {
          node_id: 82,
          type: "REFERENCE",
          name: "tomcat.apache.org",
          active: true,
        },
        { node_id: 83, type: "REFERENCE", name: "cve.mitre.org", active: true },
        { node_id: 84, type: "REFERENCE", name: "apache.org", active: false },
      ],
      [
        { node_id: 85, type: "ASN", name: "AS14618", active: true },
        { node_id: 86, type: "ASN", name: "AS16509", active: true },
      ],
      [
        { node_id: 87, type: "IP", name: "tomcat.apache.org", active: true },
        { node_id: 88, type: "IP", name: "cve.mitre.org", active: true },
      ],
    ],
    links: [
      [
        { source: 81, target: 82, active: true, verb: "affects" },
        { source: 81, target: 83, active: true, verb: "references" },
        { source: 81, target: 84, active: false, verb: "references" },
      ],
      [
        { source: 82, target: 85, active: true, verb: "hosted on" },
        { source: 83, target: 86, active: true, verb: "hosted on" },
        { source: 84, target: 85, active: false, verb: "hosted on" },
      ],
      [
        { source: 85, target: 87, active: true, verb: "resolves to" },
        { source: 86, target: 88, active: true, verb: "resolves to" },
      ],
      [],
    ],
  },
  {
    title:
      "CVE-2025-19872 allows attackers to bypass authentication in GitLab self-managed instances.",
    timestamp: 1775510000,
    report:
      "GitLab disclosed an authentication bypass vulnerability affecting self-managed instances.\n\nAttackers can forge OAuth tokens by exploiting a flaw in the SAML assertion validation logic.\n\nAll self-managed deployments should upgrade to patched versions immediately.",
    cols: [
      [
        {
          node_id: 91,
          type: "CVE",
          name: "CVE-2025-19872",
          active: true,
          severity: "HIGH",
          cvss_score: 8.6,
        },
      ],
      [
        { node_id: 92, type: "REFERENCE", name: "gitlab.com", active: true },
        {
          node_id: 93,
          type: "REFERENCE",
          name: "about.gitlab.com",
          active: true,
        },
      ],
      [{ node_id: 94, type: "ASN", name: "AS396982", active: true }],
      [
        { node_id: 95, type: "IP", name: "gitlab.com", active: true },
        { node_id: 96, type: "IP", name: "about.gitlab.com", active: true },
      ],
    ],
    links: [
      [
        { source: 91, target: 92, active: true, verb: "affects" },
        { source: 91, target: 93, active: true, verb: "references" },
      ],
      [
        { source: 92, target: 94, active: true, verb: "hosted on" },
        { source: 93, target: 94, active: true, verb: "hosted on" },
      ],
      [
        { source: 94, target: 95, active: true, verb: "resolves to" },
        { source: 94, target: 96, active: true, verb: "resolves to" },
      ],
      [],
    ],
  },
  {
    title:
      "CVE-2025-88104 Kubernetes API server vulnerability enables container escape and node takeover.",
    timestamp: 1775400000,
    report:
      "A critical vulnerability in the Kubernetes API server allows authenticated users to escape container boundaries and gain full node access.\n\nThe flaw affects all supported Kubernetes versions prior to the emergency patch.\n\nCloud providers have begun automatic patching of managed clusters.",
    cols: [
      [
        {
          node_id: 101,
          type: "CVE",
          name: "CVE-2025-88104",
          active: true,
          severity: "CRITICAL",
          cvss_score: 9.4,
        },
      ],
      [
        {
          node_id: 102,
          type: "REFERENCE",
          name: "kubernetes.io",
          active: true,
        },
        { node_id: 103, type: "REFERENCE", name: "k8s.io", active: true },
        { node_id: 104, type: "REFERENCE", name: "cncf.io", active: false },
      ],
      [
        { node_id: 105, type: "ASN", name: "AS15169", active: true },
        { node_id: 106, type: "ASN", name: "AS8075", active: true },
      ],
      [
        { node_id: 107, type: "IP", name: "kubernetes.io", active: true },
        { node_id: 108, type: "IP", name: "k8s.io", active: true },
      ],
    ],
    links: [
      [
        { source: 101, target: 102, active: true, verb: "affects" },
        { source: 101, target: 103, active: true, verb: "references" },
        { source: 101, target: 104, active: false, verb: "references" },
      ],
      [
        { source: 102, target: 105, active: true, verb: "hosted on" },
        { source: 103, target: 106, active: true, verb: "hosted on" },
      ],
      [
        { source: 105, target: 107, active: true, verb: "resolves to" },
        { source: 106, target: 108, active: true, verb: "resolves to" },
      ],
      [],
    ],
  },
  {
    title:
      "CVE-2025-67231 memory corruption in OpenSSL affects TLS 1.3 session handling.",
    timestamp: 1775290000,
    report:
      "A memory corruption vulnerability in OpenSSL's TLS 1.3 implementation can be triggered by a malformed ClientHello message.\n\nExploitation can lead to denial of service or potentially remote code execution in specific configurations.\n\nThe OpenSSL project has released patched versions.",
    cols: [
      [
        {
          node_id: 111,
          type: "CVE",
          name: "CVE-2025-67231",
          active: true,
          severity: "HIGH",
          cvss_score: 7.5,
        },
      ],
      [
        { node_id: 112, type: "REFERENCE", name: "openssl.org", active: true },
        {
          node_id: 113,
          type: "REFERENCE",
          name: "openssl-library.org",
          active: false,
        },
      ],
      [{ node_id: 114, type: "ASN", name: "AS13335", active: true }],
      [{ node_id: 115, type: "IP", name: "openssl.org", active: true }],
    ],
    links: [
      [
        { source: 111, target: 112, active: true, verb: "affects" },
        { source: 111, target: 113, active: false, verb: "references" },
      ],
      [
        { source: 112, target: 114, active: true, verb: "hosted on" },
        { source: 113, target: 114, active: false, verb: "hosted on" },
      ],
      [{ source: 114, target: 115, active: true, verb: "resolves to" }],
      [],
    ],
  },
];

const AUTO_ADVANCE_MS = 10000;

/* ─── Size presets ─── */
const SIZE_PRESETS = {
  compact: {
    CIRCLE_SIZE: 44,
    ICON_SIZE: 20,
    LABEL_MAX_WIDTH: 130,
    LABEL_FONT_PX: 11,
    LANE_GAP: 28,
    ROW_SPACING_MIN: 84,
    PADDING_X: 32,
    PADDING_Y: 28,
    TITLE_PX: 12,
    CONTROL_BTN: "h-6 w-6",
    CONTROL_ICON: 14,
    EDGE_LABEL_PX: 9,
  },
  roomy: {
    CIRCLE_SIZE: 64,
    ICON_SIZE: 30,
    LABEL_MAX_WIDTH: 190,
    LABEL_FONT_PX: 13,
    LANE_GAP: 56,
    ROW_SPACING_MIN: 128,
    PADDING_X: 56,
    PADDING_Y: 48,
    TITLE_PX: 15,
    CONTROL_BTN: "h-8 w-8",
    CONTROL_ICON: 16,
    EDGE_LABEL_PX: 11,
  },
};

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.06;
const FIT_MARGIN = 20;
const NODE_LABEL_BLOCK_HEIGHT = 30;

/* ─── Type → icon ─── */
const TYPE_ICONS = {
  ACTOR: ShieldAlert,
  CVE: Bug,
  SEVERITY: Gauge,
  CVSS_SCORE: Gauge,
  REFERENCE: Link2,
  ASN: Network,
  IP: Globe,
  TARGET: Server,
  CONSEQUENCE: Package,
  METHOD: Box,
  OTHER: Box,
};

const CVE_PATTERN = /^CVE[-\s]?\d{4}/i;

/* ─── Text measurement ─── */
const measureCtxCache = {};
const getMeasureCtx = (fontPx) => {
  if (typeof document === "undefined") return null;
  if (!measureCtxCache[fontPx]) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `500 ${fontPx}px Poppins, ui-sans-serif, system-ui, sans-serif`;
    measureCtxCache[fontPx] = ctx;
  }
  return measureCtxCache[fontPx];
};
const measureText = (text, fontPx = 11) => {
  const ctx = getMeasureCtx(fontPx);
  if (!ctx) return text.length * (fontPx * 0.59);
  return ctx.measureText(text).width;
};
const bestWrappedWidth = (name, cap, fontPx) => {
  const full = measureText(name, fontPx);
  if (full <= cap) return full;
  const words = name.split(/\s+/);
  if (words.length === 1) return full;
  let best = full;
  for (let i = 1; i < words.length; i++) {
    const l1 = measureText(words.slice(0, i).join(" "), fontPx);
    const l2 = measureText(words.slice(i).join(" "), fontPx);
    const w = Math.max(l1, l2);
    if (w < best) best = w;
  }
  return best;
};

/* ─── Relative time ─── */
const relativeTime = (unix) => {
  if (!unix) return "";
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 604800)}w ago`;
};

/* ─── Layout ─── */
function layoutGraph(graphObj, sz) {
  if (!graphObj?.cols?.length) {
    return {
      nodes: [],
      links: [],
      bbox: null,
      contentWidth: 0,
      contentHeight: 0,
    };
  }

  const cols = graphObj.cols;
  const linkColumns = graphObj.links || [];

  // Compute ideal width for every node based on its label
  const nodeDesiredWidth = (node) => {
    const wrapped = Math.min(
      bestWrappedWidth(node.name, sz.LABEL_MAX_WIDTH, sz.LABEL_FONT_PX),
      sz.LABEL_MAX_WIDTH,
    );
    return Math.max(sz.CIRCLE_SIZE, Math.ceil(wrapped));
  };

  // Column slot width = widest node in that column
  const colWidths = cols.map((colNodes) =>
    colNodes.reduce((m, n) => Math.max(m, nodeDesiredWidth(n)), sz.CIRCLE_SIZE),
  );

  let cursor = sz.PADDING_X;
  const colCenters = cols.map((_, i) => {
    const center = cursor + colWidths[i] / 2;
    cursor += colWidths[i] + sz.LANE_GAP;
    return center;
  });
  const contentWidth = cursor - sz.LANE_GAP + sz.PADDING_X;

  const tallestCol = Math.max(...cols.map((c) => c.length));
  const contentHeight =
    sz.PADDING_Y * 2 + Math.max(1, tallestCol) * sz.ROW_SPACING_MIN;
  const centerY = contentHeight / 2;

  const nodeById = {};
  const placedNodes = [];

  cols.forEach((colNodes, colIdx) => {
    const count = colNodes.length;
    const x = colCenters[colIdx];
    const colWidth = colWidths[colIdx];
    const colStackHeight = count * sz.ROW_SPACING_MIN;
    const startY = centerY - colStackHeight / 2 + sz.ROW_SPACING_MIN / 2;

    colNodes.forEach((node, idx) => {
      const placed = {
        ...node,
        x,
        y: startY + idx * sz.ROW_SPACING_MIN,
        colIdx,
        slotWidth: colWidth,
      };
      nodeById[node.node_id] = placed;
      placedNodes.push(placed);
    });
  });

  // Flatten link columns and resolve source/target positions
  const positionedLinks = [];
  linkColumns.forEach((linkArr) => {
    (linkArr || []).forEach((link) => {
      const source = nodeById[link.source];
      const target = nodeById[link.target];
      if (!source || !target) return;
      positionedLinks.push({
        source,
        target,
        verb: link.verb,
        active: link.active !== false,
      });
    });
  });

  // Bounding box
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  placedNodes.forEach((n) => {
    const halfW = Math.max(n.slotWidth, sz.CIRCLE_SIZE) / 2;
    const topY = n.y - sz.CIRCLE_SIZE / 2;
    const bottomY = n.y + sz.CIRCLE_SIZE / 2 + 6 + NODE_LABEL_BLOCK_HEIGHT;
    if (n.x - halfW < minX) minX = n.x - halfW;
    if (n.x + halfW > maxX) maxX = n.x + halfW;
    if (topY < minY) minY = topY;
    if (bottomY > maxY) maxY = bottomY;
  });
  const bbox = {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };

  return {
    nodes: placedNodes,
    links: positionedLinks,
    bbox,
    contentWidth,
    contentHeight,
  };
}

/* ─── Marquee label ─── */
const MarqueeLabel = ({ text, maxWidth, dark, sz, active }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [distance, setDistance] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;
    const check = () => {
      const textWidth = textEl.scrollWidth;
      const containerWidth = container.clientWidth;
      setDistance(
        textWidth > containerWidth + 1 ? textWidth - containerWidth : 0,
      );
    };
    check();
    const obs = new ResizeObserver(check);
    obs.observe(container);
    return () => obs.disconnect();
  }, [text]);

  const overflows = distance > 0;
  const scrollMs = Math.max(2000, (distance / 60) * 1000);
  const pauseMs = 1500;
  const totalMs = scrollMs + pauseMs * 2;
  const pauseStartPct = (pauseMs / totalMs) * 100;
  const scrollEndPct = ((pauseMs + scrollMs) / totalMs) * 100;

  const labelColor = active
    ? dark
      ? "text-white"
      : "text-mainText"
    : "text-mutedText";

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden"
      style={{
        maxWidth,
        maskImage: overflows
          ? "linear-gradient(to right, transparent 0, black 6px, black calc(100% - 6px), transparent 100%)"
          : undefined,
        WebkitMaskImage: overflows
          ? "linear-gradient(to right, transparent 0, black 6px, black calc(100% - 6px), transparent 100%)"
          : undefined,
      }}
    >
      <div
        ref={textRef}
        className={`font-poppins font-medium leading-tight ${labelColor}`}
        style={
          overflows
            ? {
                fontSize: sz.LABEL_FONT_PX,
                display: "inline-block",
                whiteSpace: "nowrap",
                animation: `cve-marquee-${Math.round(distance)} ${totalMs}ms linear infinite`,
              }
            : {
                fontSize: sz.LABEL_FONT_PX,
                textAlign: "center",
                whiteSpace: "normal",
                wordBreak: "normal",
                overflowWrap: "break-word",
              }
        }
      >
        {text}
      </div>
      {overflows && (
        <style>{`
          @keyframes cve-marquee-${Math.round(distance)} {
            0% { transform: translateX(0); }
            ${pauseStartPct.toFixed(2)}% { transform: translateX(0); }
            ${scrollEndPct.toFixed(2)}% { transform: translateX(-${distance}px); }
            100% { transform: translateX(-${distance}px); }
          }
        `}</style>
      )}
    </div>
  );
};

/* ─── Graph renderer ─── */
const Graph = ({ layout, dark, sz }) => {
  if (!layout.nodes.length) return null;

  const ACTIVE_EDGE = dark ? "rgba(56,189,248,0.6)" : "rgba(71,85,105,0.6)";
  const INACTIVE_EDGE = dark
    ? "rgba(148,163,184,0.22)"
    : "rgba(148,163,184,0.4)";
  const LABEL_BG = dark ? "rgba(14,19,28,0.92)" : "rgba(228,234,242,0.95)";

  const ACTIVE_BORDER = dark
    ? "rgba(14,165,233,0.55)"
    : "rgba(120,134,152,0.65)";
  const INACTIVE_BORDER = dark
    ? "rgba(148,163,184,0.22)"
    : "rgba(148,163,184,0.35)";
  const ACTIVE_BG = dark ? "rgba(14,165,233,0.10)" : "rgba(84,98,118,0.08)";
  const INACTIVE_BG = dark
    ? "rgba(148,163,184,0.04)"
    : "rgba(148,163,184,0.05)";

  const activeArrowId = `cve-arrow-active-${dark ? "d" : "l"}`;
  const inactiveArrowId = `cve-arrow-inactive-${dark ? "d" : "l"}`;

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-0"
        width={layout.contentWidth}
        height={layout.contentHeight}
        style={{ overflow: "visible" }}
      >
        <defs>
          <marker
            id={activeArrowId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={ACTIVE_EDGE} />
          </marker>
          <marker
            id={inactiveArrowId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={INACTIVE_EDGE} />
          </marker>
        </defs>
        {layout.links.map((link, i) => {
          const midX = (link.source.x + link.target.x) / 2;
          const midY = (link.source.y + link.target.y) / 2;
          const showLabel = link.verb && link.verb.length <= 22 && link.active;
          const stroke = link.active ? ACTIVE_EDGE : INACTIVE_EDGE;

          return (
            <g key={i}>
              <line
                x1={link.source.x}
                y1={link.source.y}
                x2={link.target.x}
                y2={link.target.y}
                stroke={stroke}
                strokeWidth={1.5}
                strokeDasharray={link.active ? undefined : "4 4"}
                markerEnd={`url(#${link.active ? activeArrowId : inactiveArrowId})`}
              />
              {showLabel && (
                <g>
                  <rect
                    x={midX - measureText(link.verb, sz.EDGE_LABEL_PX) / 2 - 4}
                    y={midY - sz.EDGE_LABEL_PX / 2 - 2}
                    width={measureText(link.verb, sz.EDGE_LABEL_PX) + 8}
                    height={sz.EDGE_LABEL_PX + 4}
                    rx={3}
                    fill={LABEL_BG}
                  />
                  <text
                    x={midX}
                    y={midY + sz.EDGE_LABEL_PX / 2 - 1}
                    textAnchor="middle"
                    className="font-poppins"
                    style={{
                      fontSize: sz.EDGE_LABEL_PX,
                      fill: stroke,
                      fontWeight: 600,
                    }}
                  >
                    {link.verb}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {layout.nodes.map((node) => {
        const isCVE = CVE_PATTERN.test(node.name);
        const active = node.active !== false;
        const Icon = TYPE_ICONS[node.type] || TYPE_ICONS.OTHER;

        return (
          <div
            key={node.node_id}
            title={`${node.name} · ${node.type}`}
            className="absolute flex flex-col items-center gap-1.5 font-poppins"
            style={{
              left: node.x,
              top: node.y,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div
              className="flex shrink-0 items-center justify-center rounded-full backdrop-blur-sm"
              style={{
                width: sz.CIRCLE_SIZE,
                height: sz.CIRCLE_SIZE,
                border: `1px solid ${active ? ACTIVE_BORDER : INACTIVE_BORDER}`,
                background: active ? ACTIVE_BG : INACTIVE_BG,
                opacity: active ? 1 : 0.55,
              }}
            >
              <Icon
                size={sz.ICON_SIZE}
                strokeWidth={2}
                color={active ? "white" : dark ? "#94a3b8" : "#94a3b8"}
                aria-hidden
              />
            </div>
            {isCVE ? (
              <div
                className={`font-poppins font-mono font-medium ${
                  active
                    ? dark
                      ? "text-white"
                      : "text-mainText"
                    : "text-mutedText"
                }`}
                style={{
                  maxWidth: node.slotWidth,
                  textAlign: "center",
                  fontSize: sz.LABEL_FONT_PX - 1,
                  opacity: active ? 1 : 0.65,
                }}
              >
                {node.name}
              </div>
            ) : (
              <div style={{ opacity: active ? 1 : 0.65 }}>
                <MarqueeLabel
                  text={node.name}
                  maxWidth={node.slotWidth}
                  dark={dark}
                  sz={sz}
                  active={active}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

/* ─── Pan/Zoom stage ─── */
const PanZoomStage = ({
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
  onInteract,
  dark,
  children,
}) => {
  const fitZoom = useMemo(() => {
    if (!contentWidth || !contentHeight) return 1;
    const availW = containerWidth - FIT_MARGIN * 2;
    const availH = containerHeight - FIT_MARGIN * 2;
    const scaleX = availW / contentWidth;
    const scaleY = availH / contentHeight;
    return Math.max(0.1, Math.min(1, Math.min(scaleX, scaleY)));
  }, [containerWidth, containerHeight, contentWidth, contentHeight]);

  const [zoom, setZoom] = useState(fitZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const surfaceRef = useRef(null);

  useEffect(() => {
    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
  }, [fitZoom, contentWidth, contentHeight]);

  const clampPan = useCallback(
    (nextPan, nextZoom) => {
      if (!contentWidth || !contentHeight) return nextPan;
      const scaledW = contentWidth * nextZoom;
      const scaledH = contentHeight * nextZoom;
      const slackX = Math.max(0, (scaledW - containerWidth) / 2);
      const slackY = Math.max(0, (scaledH - containerHeight) / 2);
      const extraX = containerWidth * 0.15;
      const extraY = containerHeight * 0.15;
      const maxX = slackX + extraX;
      const maxY = slackY + extraY;
      return {
        x: Math.max(-maxX, Math.min(maxX, nextPan.x)),
        y: Math.max(-maxY, Math.min(maxY, nextPan.y)),
      };
    },
    [contentWidth, contentHeight, containerWidth, containerHeight],
  );

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cursorX = e.clientX - rect.left - containerWidth / 2;
      const cursorY = e.clientY - rect.top - containerHeight / 2;
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const nextZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, zoom * (1 + delta)),
      );
      if (nextZoom === zoom) return;
      const scale = nextZoom / zoom;
      const nextPan = {
        x: cursorX - (cursorX - pan.x) * scale,
        y: cursorY - (cursorY - pan.y) * scale,
      };
      setZoom(nextZoom);
      setPan(clampPan(nextPan, nextZoom));
      onInteract?.();
    },
    [zoom, pan, containerWidth, containerHeight, clampPan, onInteract],
  );

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
  };
  const handlePointerMove = (e) => {
    if (!isDragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.clientX;
    const dy = e.clientY - dragStartRef.current.clientY;
    if (!dragStartRef.current.moved && Math.hypot(dx, dy) > 3) {
      dragStartRef.current.moved = true;
      onInteract?.();
    }
    setPan(
      clampPan(
        {
          x: dragStartRef.current.panX + dx,
          y: dragStartRef.current.panY + dy,
        },
        zoom,
      ),
    );
  };
  const handlePointerUp = (e) => {
    if (!isDragging) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    setIsDragging(false);
    dragStartRef.current = null;
  };
  const handleDoubleClick = () => {
    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
    onInteract?.();
  };

  const offsetX = (containerWidth - contentWidth) / 2 + pan.x;
  const offsetY = (containerHeight - contentHeight) / 2 + pan.y;

  const cursor = isDragging ? "grabbing" : "grab";
  const isAtFit =
    Math.abs(zoom - fitZoom) < 0.001 && pan.x === 0 && pan.y === 0;

  const zoomBadgeClasses = dark
    ? "border-velBlue/30 bg-velBlue/[0.08] text-velBlue"
    : "border-surfaceBorder bg-surface/90 text-mutedText";

  return (
    <div
      ref={surfaceRef}
      className="absolute inset-0 overflow-hidden"
      style={{ cursor, touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    >
      <div
        style={{
          position: "absolute",
          left: offsetX,
          top: offsetY,
          width: contentWidth,
          height: contentHeight,
          transform: `scale(${zoom})`,
          transformOrigin: "center center",
        }}
      >
        {children}
      </div>

      {!isAtFit && (
        <div
          className={`pointer-events-none absolute left-3 top-3 rounded-md border px-2 py-0.5 font-poppins font-mono text-[10px] backdrop-blur-sm ${zoomBadgeClasses}`}
        >
          {Math.round(zoom * 100)}%
        </div>
      )}
    </div>
  );
};

/* ─── Summary overlay ─── */
const SummaryOverlay = ({ report, onClose, dark, size }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const paragraphs = (report || "").split(/\n\n+/).filter(Boolean);
  const eyebrowColor = dark ? "text-velBlue" : "text-mutedText";
  const bodyPx = size === "roomy" ? 13 : 11;
  const paragraphGap = size === "roomy" ? 14 : 10;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col font-poppins"
      style={{
        background: dark ? "rgba(14,19,28,0.65)" : "rgba(213,221,232,0.65)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-mainDivider px-4 py-2">
        <div className="flex items-center gap-1.5">
          <FileText
            size={11}
            strokeWidth={2}
            className={eyebrowColor}
            aria-hidden
          />
          <span
            className={`text-[9px] font-semibold uppercase tracking-[0.14em] ${eyebrowColor}`}
          >
            Summary
          </span>
        </div>
        <button
          onClick={onClose}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2"
          title="Close summary"
          aria-label="Close summary"
        >
          <X size={13} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ scrollbarWidth: "thin" }}
      >
        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => (
            <p
              key={i}
              className="font-poppins leading-relaxed text-mainText"
              style={{
                fontSize: bodyPx,
                marginBottom: i === paragraphs.length - 1 ? 0 : paragraphGap,
              }}
            >
              {p}
            </p>
          ))
        ) : (
          <p className="font-poppins text-[11px] text-mutedText">
            No summary available for this story.
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Panel ─── */
const CVEGraphPanel = ({
  data,
  dark = false,
  size = "compact",
  sizeOverrides,
}) => {
  const sz = useMemo(() => {
    const base = SIZE_PRESETS[size] || SIZE_PRESETS.compact;
    return { ...base, ...(sizeOverrides || {}) };
  }, [size, sizeOverrides]);

  // Use provided data, fall back to dummy until API is live
  const graphs = useMemo(() => {
    const source = Array.isArray(data) && data.length > 0 ? data : DUMMY_DATA;
    return source.filter((g) => Array.isArray(g?.cols) && g.cols.length > 0);
  }, [data]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showSummary, setShowSummary] = useState(false);

  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setActiveIndex(0);
    setProgress(0);
    setShowSummary(false);
  }, [graphs]);

  useEffect(() => {
    setShowSummary(false);
  }, [activeIndex]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setDims({ width: el.clientWidth, height: el.clientHeight });
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [graphs]);

  const shouldAutoplay = isPlaying && !showSummary;

  useEffect(() => {
    if (!graphs || graphs.length <= 1 || !shouldAutoplay) {
      setProgress(0);
      return;
    }
    const startTime = Date.now();
    let rafId;
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(elapsed / AUTO_ADVANCE_MS, 1);
      setProgress(p);
      if (p >= 1) setActiveIndex((i) => (i + 1) % graphs.length);
      else rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [graphs, shouldAutoplay, activeIndex]);

  const pauseAutoplay = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handlePrev = useCallback(() => {
    if (!graphs || graphs.length <= 1) return;
    setActiveIndex((i) => (i - 1 + graphs.length) % graphs.length);
    setProgress(0);
  }, [graphs]);
  const handleNext = useCallback(() => {
    if (!graphs || graphs.length <= 1) return;
    setActiveIndex((i) => (i + 1) % graphs.length);
    setProgress(0);
  }, [graphs]);
  const togglePlay = useCallback(() => setIsPlaying((p) => !p), []);
  const toggleSummary = useCallback(() => setShowSummary((s) => !s), []);

  const currentGraph = graphs[activeIndex];
  const hasMultiple = graphs.length > 1;
  const hasReport =
    currentGraph?.report && currentGraph.report.trim().length > 0;

  const layoutData = useMemo(() => {
    if (!currentGraph) return null;
    return layoutGraph(currentGraph, sz);
  }, [currentGraph, sz]);

  const progressBarColor = dark ? "bg-velBlue" : "bg-mutedText";
  const btnSizeClass = sz.CONTROL_BTN;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden font-poppins font-normal">
      <div className="flex shrink-0 items-start gap-3 border-b border-mainDivider px-4 py-2">
        {currentGraph?.title && (
          <p
            className="flex-1 font-poppins font-normal leading-snug text-mainText"
            style={{
              fontSize: sz.TITLE_PX,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {currentGraph.title}
          </p>
        )}
        {currentGraph?.timestamp && (
          <span className="shrink-0 font-poppins text-[10px] text-mutedText">
            {relativeTime(currentGraph.timestamp)}
          </span>
        )}
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        {graphs.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-poppins text-[11px] text-mutedText">
              No graph data available
            </span>
          </div>
        ) : layoutData && dims.width > 0 && dims.height > 0 ? (
          <>
            <PanZoomStage
              key={activeIndex}
              containerWidth={dims.width}
              containerHeight={dims.height}
              contentWidth={layoutData.contentWidth}
              contentHeight={layoutData.contentHeight}
              onInteract={pauseAutoplay}
              dark={dark}
            >
              <Graph layout={layoutData} dark={dark} sz={sz} />
            </PanZoomStage>

            {showSummary && currentGraph && (
              <SummaryOverlay
                report={currentGraph.report}
                onClose={() => setShowSummary(false)}
                dark={dark}
                size={size}
              />
            )}
          </>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-mainDivider">
        {hasMultiple && (
          <div className="relative h-0.5 w-full bg-mainDivider/50">
            <div
              className={`absolute left-0 top-0 h-full ${progressBarColor}`}
              style={{
                width: `${progress * 100}%`,
                transition: progress === 0 ? "none" : "width 50ms linear",
              }}
            />
          </div>
        )}
        <div className="flex items-center px-3 py-2">
          <div className="flex flex-1 basis-0 items-center gap-2">
            {graphs.length > 0 && (
              <span
                className="font-poppins font-mono text-[10px] text-mutedText tabular-nums"
                style={{ minWidth: "3.5ch", display: "inline-block" }}
              >
                {activeIndex + 1} / {graphs.length}
              </span>
            )}
            {hasMultiple && shouldAutoplay && (
              <span
                className="font-poppins font-mono text-[10px] text-mutedText tabular-nums"
                style={{ minWidth: "2.5ch", display: "inline-block" }}
              >
                {Math.max(
                  0,
                  Math.ceil(
                    (AUTO_ADVANCE_MS - progress * AUTO_ADVANCE_MS) / 1000,
                  ),
                )}
                s
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-center gap-2">
            {hasMultiple && (
              <>
                <button
                  onClick={handlePrev}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2`}
                  title="Previous graph"
                >
                  <ChevronLeft
                    size={sz.CONTROL_ICON}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
                <button
                  onClick={togglePlay}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg transition cursor-pointer hover:bg-btnHover outline-none ring-inputFocus/60 focus-visible:ring-2 ${
                    isPlaying
                      ? dark
                        ? "text-velBlue"
                        : "text-mainText"
                      : "text-mutedText hover:text-mainText"
                  }`}
                  title={isPlaying ? "Pause autoplay" : "Resume autoplay"}
                >
                  {isPlaying ? (
                    <Pause
                      size={sz.CONTROL_ICON - 1}
                      strokeWidth={2}
                      aria-hidden
                    />
                  ) : (
                    <Play
                      size={sz.CONTROL_ICON - 1}
                      strokeWidth={2}
                      aria-hidden
                    />
                  )}
                </button>
                <button
                  onClick={handleNext}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2`}
                  title="Next graph"
                >
                  <ChevronRight
                    size={sz.CONTROL_ICON}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              </>
            )}
          </div>

          <div className="flex flex-1 basis-0 items-center justify-end gap-2">
            {hasReport && (
              <button
                onClick={toggleSummary}
                className={`flex items-center gap-1.5 rounded-lg border px-2 text-[10px] font-medium transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2 ${
                  size === "roomy" ? "h-8 text-[11px]" : "h-6"
                } ${
                  showSummary
                    ? dark
                      ? "border-velBlue/50 bg-velBlue/[0.12] text-velBlue"
                      : "border-surfaceBorder bg-btnHover text-mainText"
                    : "border-inputBorder text-mutedText hover:border-surfaceBorder hover:bg-btnHover hover:text-mainText"
                }`}
                title={showSummary ? "Hide summary" : "Show summary"}
                aria-pressed={showSummary}
              >
                <FileText size={11} strokeWidth={2} aria-hidden />
                {showSummary ? "Hide Summary" : "Show Summary"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVEGraphPanel;
